import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "antd";
import {
  BtnRow,
  CandidateMeta,
  CandidateOption,
  ErrorNotice,
  GuardrailNotice,
  MutedText,
  PrimaryBtn,
  ResultMeta,
  ResultName,
  SearchResultEmpty,
  SearchResultItem,
  SearchResults,
  SearchWrapper,
  SecondaryBtn,
  TextInput,
  WarningChip,
} from "../MailIntakeStyled";
import { searchMattersAPI } from "../../../service/mailIntake";
import {
  ACKNOWLEDGEMENT_FOR_REASON,
  confirmEfolderJob,
  conflictPayload,
  describeReasonCode,
  errorMessage,
  errorReasonCode,
  errorStatus,
  reassignEfolderJob,
  preferredCandidate,
} from "../../../service/efolderUpload";

const MIN_QUERY = 2;
const SEARCH_DEBOUNCE_MS = 300;

const isClosed = (summary) => {
  const s = (summary?.status || "").trim().toLowerCase();
  return s !== "" && s !== "open";
};
// The backend decides what counts as Agency (pipeline mode); on screen a
// case type without "Agency" in it gets a chip so the person looks twice.
const isNonAgency = (summary) => {
  const type = summary?.case_type || "";
  return type !== "" && !/agency/i.test(type);
};

// Chips next to a candidate (spec 9.2.4).
export const candidateWarnings = (candidate, job) => {
  const warnings = [];
  const summary = candidate?.matter_summary;
  if (isClosed(summary)) warnings.push(summary.status);
  if (isNonAgency(summary)) warnings.push(summary.case_type);
  if (job?.match?.reason_code === "multiple_agency_matters") warnings.push("Multiple Agency matters");
  if (job?.match?.identical_zip_job_id) warnings.push("Identical zip already processed");
  return warnings;
};

// Which candidate starts selected: the same one the row describes.
export const defaultSelection = (job) => preferredCandidate(job)?.matter_id ?? null;

const GUARDRAIL_TEXT = {
  closed_only: "That matter is not open.",
  non_agency_only: "That matter is not a VA Agency matter.",
};

/**
 * Confirm / change-matter dialog for a central eFolder job (spec 9.2.4).
 * Pick one of the backend's candidates or search any matter; a closed or
 * non-Agency choice comes back as a 422 the user acknowledges explicitly.
 */
const EfolderConfirmModal = ({ job, mode = "confirm", onClose, onDone }) => {
  const candidates = useMemo(() => job?.match?.candidates || [], [job]);
  const [selectedId, setSelectedId] = useState(() => defaultSelection(job));
  const [picked, setPicked] = useState(null); // a matter chosen from search
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [guardrail, setGuardrail] = useState(null); // { reasonCode, message }
  // Every warning the user has already accepted for the selected matter; a
  // closed non-Agency matter raises two in turn and the server wants both.
  const [acknowledged, setAcknowledged] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      setResults(null);
      setSearching(false);
      return undefined;
    }
    let live = true;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await searchMattersAPI(q);
        if (live) setResults(response?.data?.results || []);
      } catch {
        if (live) setResults([]);
      } finally {
        if (live) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query]);

  const choose = (matterId) => {
    setSelectedId(matterId);
    setGuardrail(null);
    setAcknowledged([]);
    setError(null);
  };

  const pickResult = (matter) => {
    setPicked(matter);
    setQuery("");
    setResults(null);
    choose(matter.id);
  };

  const submit = async (acknowledgeWarning = null) => {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    const action = mode === "reassign" ? reassignEfolderJob : confirmEfolderJob;
    const acks = acknowledgeWarning && !acknowledged.includes(acknowledgeWarning)
      ? [...acknowledged, acknowledgeWarning]
      : acknowledged;
    if (acks !== acknowledged) setAcknowledged(acks);
    try {
      const payload = await action(job.job_id, {
        matterId: selectedId,
        acknowledgeWarning: acks.length === 0 ? null : acks.length === 1 ? acks[0] : acks,
      });
      onDone(payload);
    } catch (err) {
      const status = errorStatus(err);
      const reasonCode = errorReasonCode(err);
      if (status === 422 && reasonCode && ACKNOWLEDGEMENT_FOR_REASON[reasonCode]) {
        setGuardrail({ reasonCode, message: GUARDRAIL_TEXT[reasonCode] || errorMessage(err) });
      } else if (conflictPayload(err)) {
        // Someone else already decided this one; show what they did.
        onDone(conflictPayload(err), { conflict: errorMessage(err) });
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "reassign" ? "Change the matter for this zip" : "Confirm the matter for this zip";
  const verb = mode === "reassign" ? "Change matter" : "Confirm";
  const zipLabel = job?.redact || !job?.zip_name ? `Zip #${job?.job_id}` : job.zip_name;
  const reason = describeReasonCode(job?.match?.reason_code);

  return (
    <Modal open title={title} onCancel={onClose} footer={null} width={560}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="efolder-confirm-modal">
        <div>
          <strong>{zipLabel}</strong>
          {reason && <div><MutedText>{reason}</MutedText></div>}
        </div>

        {candidates.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }} role="radiogroup" aria-label="Candidate matters">
            {candidates.map((candidate) => {
              const summary = candidate.matter_summary;
              const id = candidate.matter_id;
              const selected = String(selectedId) === String(id);
              const warnings = candidateWarnings(candidate, job);
              return (
                <CandidateOption key={id} $selected={selected}>
                  <input
                    type="radio"
                    name="efolder-candidate"
                    value={id}
                    checked={selected}
                    onChange={() => choose(id)}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div>
                      {summary?.display_name || (job?.redact ? "Matter (hidden)" : `Matter ${id}`)}
                      {candidate.confidence != null && (
                        <MutedText style={{ marginLeft: 8 }}>{Math.round(candidate.confidence * 100)}%</MutedText>
                      )}
                    </div>
                    {summary && (
                      <CandidateMeta>
                        {[summary.case_type, summary.status, summary.principal_attorney].filter(Boolean).join(" · ")}
                      </CandidateMeta>
                    )}
                    {candidate.reason && <CandidateMeta>{candidate.reason}</CandidateMeta>}
                    {warnings.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        {warnings.map((w) => <WarningChip key={w}>{w}</WarningChip>)}
                      </div>
                    )}
                  </div>
                </CandidateOption>
              );
            })}
          </div>
        ) : (
          <MutedText>No candidate matters. Search for the matter below.</MutedText>
        )}

        {picked && (
          <CandidateOption $selected={String(selectedId) === String(picked.id)}>
            <input
              type="radio"
              name="efolder-candidate"
              value={picked.id}
              checked={String(selectedId) === String(picked.id)}
              onChange={() => choose(picked.id)}
            />
            <div>
              <div>{picked.display_name || picked.client_name}</div>
              <CandidateMeta>
                {[picked.case_type, picked.status].filter(Boolean).join(" · ") || "From search"}
              </CandidateMeta>
            </div>
          </CandidateOption>
        )}

        <SearchWrapper>
          <TextInput
            aria-label="Search matters"
            placeholder="Search another matter (name or file number)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query.trim().length >= MIN_QUERY && (
            <SearchResults>
              {searching && results === null && <SearchResultEmpty>Searching…</SearchResultEmpty>}
              {results && results.length === 0 && !searching && <SearchResultEmpty>No matters found</SearchResultEmpty>}
              {results &&
                results.map((m) => (
                  <SearchResultItem key={m.id} onClick={() => pickResult(m)} role="option" aria-selected={false}>
                    <ResultName>{m.display_name || m.client_name}</ResultName>
                    <ResultMeta>{[m.client_name, m.case_type, m.status].filter(Boolean).join(" · ")}</ResultMeta>
                  </SearchResultItem>
                ))}
            </SearchResults>
          )}
        </SearchWrapper>

        {guardrail && (
          <GuardrailNotice role="alert">
            {guardrail.message} Documents would still extract into it. {verb} anyway?
          </GuardrailNotice>
        )}
        {error && <ErrorNotice role="alert">{error}</ErrorNotice>}

        <BtnRow>
          {guardrail ? (
            <PrimaryBtn
              type="button"
              disabled={busy}
              onClick={() => submit(ACKNOWLEDGEMENT_FOR_REASON[guardrail.reasonCode])}
            >
              {busy ? "Working…" : `${verb} anyway`}
            </PrimaryBtn>
          ) : (
            <PrimaryBtn type="button" disabled={busy || !selectedId} onClick={() => submit(null)}>
              {busy ? "Working…" : verb}
            </PrimaryBtn>
          )}
          <SecondaryBtn type="button" onClick={onClose} disabled={busy}>Cancel</SecondaryBtn>
        </BtnRow>
      </div>
    </Modal>
  );
};

EfolderConfirmModal.propTypes = {
  job: PropTypes.object.isRequired,
  mode: PropTypes.oneOf(["confirm", "reassign"]),
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default EfolderConfirmModal;
